import { apiBuilder, prefixApi } from "@zodios/core";
import { z } from "zod";

const advertiserAttributeSchema = z.object({
  advertiserId: z.number(),
  advertiserName: z.string().optional(),
});

const advertiserAttributesPollRequestSchema = z.object({
  force_refresh: z.boolean().optional(),
});

const baseResponseSchema = z.object({
  status: z.string(),
  snapshot_id: z.string(),
});

const successResponseSchema = baseResponseSchema.extend({
  status: z.literal("done"),
  created_on: z.string(), // API returns non-ISO datetime format
  updated_on: z.string(), // API returns non-ISO datetime format
  unused_advertiser_attributes: z.array(advertiserAttributeSchema),
  all_advertiser_attributes: z.array(advertiserAttributeSchema),
});

const pendingResponseSchema = baseResponseSchema.extend({
  status: z.union([z.literal("pending"), z.literal("processing")]),
  created_on: z.string(), // API returns non-ISO datetime format
  message: z.string(),
});

// Failed response with cooldown (status: "failed")
const failedResponseSchema = baseResponseSchema.extend({
  status: z.literal("failed"),
  error_message: z.string(),
  failed_at: z.string(), // API returns non-ISO datetime format
  can_retry_after: z.string(), // API returns non-ISO datetime format
  message: z.string(),
});

const errorResponseSchema = z.object({
  error: z.string(),
  detail: z.string().optional(),
  redirectConfig: z
    .object({
      successPath: z.string(),
      errorPath: z.string(),
    })
    .optional(),
});

// Union of all possible success responses (200 status)
const pollResponseSchema = z.union([successResponseSchema, pendingResponseSchema, failedResponseSchema]);

const walmartOnboardingStatusSchema = z.object({
  partner_access_state: z.string(),
  advertiser_id_provided: z.boolean().optional(),
  marketplace_credentials_provided: z.boolean().optional(),
  seller_type: z.string(),
  seller_id: z.string().nullable(),
  advertiser_id: z.string().nullable(),
});

export const walmartMarketplaceStatusSchema = z.object({
  auth_method: z.enum(["oauth", "client_credentials"]).nullable(),
  is_connected: z.boolean(),
  refresh_token_expires_at: z.string().datetime().optional(),
});

const walmartOnboardingActionSchema = z.union([
  z.object({
    action: z.enum([
      "mark_onboarding_complete",
      "mark_secondary_channel_onboarding_complete",
      "mark_advertising_partner_complete",
    ]),
  }),
  z.object({
    action: z.literal("add_new_walmart_profile"),
    new_org_name: z.string(),
  }),
]);

const walmartOnboardingResponseSchema = z.object({ org_id: z.number().optional() });

const submitAdvertiserIdRequestSchema = z.object({
  advertiser_id: z.string(),
  specified_org_name: z.string().optional(),
  standalone: z.boolean().optional().default(false),
  profile_type: z.string().optional().default("seller"),
  onboarding: z.boolean().optional().default(false),
  created_by: z.number().optional(),
  secondary_channel_onboarding: z.boolean().optional().default(false),
  // Save an ID Walmart has not confirmed, on the user's assurance that they granted access.
  force: z.boolean().optional(),
});

const updateAdvertiserIdRequestSchema = z.object({
  advertiser_id: z.string(),
  force: z.boolean().optional(),
});

// `saved_unverified` is a forced save: stored, but Walmart has not confirmed access to it yet.
const advertiserSavedSchema = z.object({
  verified: z.boolean(),
  code: z.enum(["verified", "saved_unverified"]),
  advertiser_id: z.string(),
});

// Returned with a 400 by every endpoint that probes Walmart with an advertiser ID. `retryable`
// distinguishes a grant that has not propagated yet from an ID that will never work.
const advertiserVerificationFailureSchema = z.object({
  verified: z.literal(false),
  code: z.enum(["invalid_advertiser_id", "access_not_granted", "rate_limited", "walmart_unavailable"]),
  message: z.string(),
  retryable: z.boolean(),
});

export type AdvertiserAttribute = z.infer<typeof advertiserAttributeSchema>;
export type WalmartOnboardingAction = z.infer<typeof walmartOnboardingActionSchema>;
export type SubmitAdvertiserIdRequest = z.infer<typeof submitAdvertiserIdRequestSchema>;
export type AdvertiserVerificationFailure = z.infer<typeof advertiserVerificationFailureSchema>;

export const walmartApiDefinitions = prefixApi(
  "/walmart",
  apiBuilder({
    alias: "pollAdvertiserAttributes",
    method: "post",
    path: "/advertiser_attributes/poll",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: advertiserAttributesPollRequestSchema,
      },
    ],
    status: 200,
    response: pollResponseSchema,
    errors: [
      {
        status: 400,
        schema: errorResponseSchema,
      },
      {
        status: 500,
        schema: errorResponseSchema,
      },
    ],
  })
    .addEndpoint({
      alias: "getWalmartOnboardingStatus",
      method: "get",
      path: "/auth/onboarding",
      response: walmartOnboardingStatusSchema,
    })
    .addEndpoint({
      alias: "getWalmartMarketplaceStatus",
      method: "get",
      path: "/auth/marketplace",
      response: walmartMarketplaceStatusSchema,
    })
    .addEndpoint({
      alias: "deleteWalmartMarketplace",
      method: "delete",
      path: "/auth/marketplace",
      status: 200,
      response: z.object({}),
    })
    .addEndpoint({
      alias: "markWalmartOnboardingStep",
      method: "post",
      path: "/auth/onboarding",
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: walmartOnboardingActionSchema,
        },
      ],
      response: walmartOnboardingResponseSchema,
    })
    .addEndpoint({
      alias: "submitWalmartAdvertiserId",
      method: "post",
      path: "/auth/advertising",
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: submitAdvertiserIdRequestSchema,
        },
      ],
      response: advertiserSavedSchema,
      errors: [
        {
          status: 400,
          schema: advertiserVerificationFailureSchema,
        },
      ],
    })
    .addEndpoint({
      alias: "updateWalmartAdvertiserId",
      method: "put",
      path: "/auth/advertising",
      parameters: [
        {
          name: "body",
          type: "Body",
          schema: updateAdvertiserIdRequestSchema,
        },
      ],
      response: advertiserSavedSchema,
      errors: [
        {
          status: 400,
          schema: advertiserVerificationFailureSchema,
        },
      ],
    })
    .addEndpoint({
      alias: "initWalmartOauth",
      method: "get",
      path: "/oauth/authorize",
      parameters: [
        { name: "org_id", type: "Query", schema: z.string().optional() },
        { name: "success_path", type: "Query", schema: z.string() },
        { name: "error_path", type: "Query", schema: z.string() },
      ],
      response: z.object({ redirect_url: z.string() }),
      errors: [
        { status: 400, schema: errorResponseSchema },
        { status: 403, schema: errorResponseSchema },
        { status: 404, schema: errorResponseSchema },
      ],
    })
    .build(),
);
